/**
 * Qué se le muestra al usuario del Explorador cuando el backend rechaza la consulta por un motivo
 * que él puede corregir.
 *
 * Por defecto el Explorador traduce el error a un `kind` de empty state con copy fijo y descarta el
 * `error_message` del server — es la política del Frontend (aislamiento cross-agency: un mensaje
 * crudo puede nombrar cosas de otra agencia, o llegar en inglés desde DRF).
 *
 * La excepción son los errores del QUERY de GA4: textos escritos a mano en el backend
 * (`looker_cc/helpers/upstream_errors.py`) que nombran **los campos que el propio usuario acaba de
 * elegir** y dicen cuántos sacar. Ahí el mensaje del server es lo único que dice QUÉ arreglar, y
 * descartarlo salía caro: pedir 10 dimensiones GA4 devolvía «Google Analytics 4 acepta como máximo
 * 9 dimensiones por consulta, y esta pide 10: Fecha, GA4__Fuente, … Hay que quitar al menos 1» y la
 * pantalla mostraba «Algo falló al consultar el Nodo de Datos. Reintentá en un momento», que manda
 * a reintentar una consulta que va a fallar siempre igual.
 *
 * Es el mismo criterio —y la misma forma— que `informes/state/renderError.js` para los errores del
 * validador de layout. Las allowlists son distintas a propósito: cada pantalla decide qué códigos
 * sabe explicar.
 */

/** Códigos TIPADOS cuyo `error_message` del backend se muestra tal cual. */
export const SERVER_MESSAGE_CODES = new Set([
    'GA4_TOO_MANY_FIELDS',
    'GA4_INCOMPATIBLE_FIELDS',
]);

/**
 * El texto del backend para este error, o `null` si corresponde el copy fijo del empty state.
 *
 * @param response la respuesta de `APIClient` (o el error rechazado, que tiene la misma forma).
 * @param language el idioma activo de la UI (`i18n.language`). Los mensajes del backend están
 *        escritos en español, así que sólo se muestran con la UI en español: en inglés meterían un
 *        texto español en una pantalla en inglés. El copy fijo al que se cae no afirma una causa
 *        que no conoce, así que el fallback es honesto — pierde el detalle, no lo inventa.
 */
export function typedQueryErrorMessage(response, language) {
    if (!SERVER_MESSAGE_CODES.has(typedErrorCode(response))) return null;
    if (!isSpanish(language)) return null;
    // `raw_body.error_message` es el texto que escribió el backend. El `error_message` de primer
    // nivel NO sirve: `APIClient.handleResponse` lo arma como `detail || message || error_message
    // || <genérico>`, así que puede ser el `detail` en inglés de DRF o un relleno que inventa el
    // propio cliente, y no hay forma de distinguirlos mirándolo.
    const rawBody = response && response.raw_body;
    const message = rawBody && rawBody.error_message;
    return typeof message === 'string' && message.trim() ? message : null;
}

/**
 * El código tipado que devolvió el backend (`ErrorResponse._data()` lo escribe en las dos claves).
 * `error_code` primero porque es la que el ecosistema lee; `status` es la que mira el Frontend.
 */
export function typedErrorCode(response) {
    if (!response) return null;
    const rawBody = response.raw_body || {};
    const code = response.error_code || rawBody.error_code || response.status || rawBody.status;
    // `error_code` llega como número en algunos caminos legacy del APIClient (409, 502…): esos no
    // son códigos tipados.
    return typeof code === 'string' ? code : null;
}

/**
 * Los parámetros de un error tipado, para armar el texto en el Frontend (tanda «Filtros que se
 * respetan», `utils/filterNotes.js::filterErrorText`): hoy, el campo que el error nombra. Se leen
 * del cuerpo CRUDO (`raw_body.error_params`), como el mensaje; sin ellos el texto no nombra el
 * campo, que es el lado seguro para equivocarse.
 */
export function typedErrorParams(response) {
    const rawBody = (response && response.raw_body) || {};
    const params = rawBody.error_params || rawBody.params || {};
    // `invalid_field` es la clave que el nodo emite hoy para `INVALID_FIELD` (review C-fe-03); el
    // Backend la reenvía en la raíz del cuerpo.
    const field = params.field || params.field_name || params.fieldName
        || rawBody.invalid_field || rawBody.field || rawBody.field_name || null;
    return {field: typeof field === 'string' && field.trim() ? field : null};
}

// Sin idioma declarado se asume español, que es la lengua por defecto del producto.
function isSpanish(language) {
    return language === undefined || language === null
        || String(language).toLowerCase().startsWith('es');
}
